import * as i0 from '@angular/core';
import { EventEmitter, Component, Input, Output, NgModule } from '@angular/core';
import { ReplaySubject, BehaviorSubject, combineLatest, EMPTY, of } from 'rxjs';
import '@angular/forms';
import { map, switchMap, startWith, distinctUntilChanged, tap } from 'rxjs/operators';
import { FFObject, randomString, nextOnChange } from '@ff/utils';
import * as i1 from '@ff/localization';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';

class FFLocalizedMatErrorComponent {
    constructor(_validationService, _localizationQuery, _languagesQuery) {
        this._validationService = _validationService;
        this._localizationQuery = _localizationQuery;
        this._languagesQuery = _languagesQuery;
        /** container for @Input() formControlRef */
        this._formControlRef$$ = new ReplaySubject(1);
        /** container for @Input() errorMessages */
        this._errorMessages$$ = new BehaviorSubject(null);
        /** container for detecting value changing of dependent controls */
        this._updateValidityDependencies$$ = new BehaviorSubject('');
        this._errorMessageContainer$$ = combineLatest([
            this._errorMessages$$,
            this._localizationQuery.selectAll(),
            this._languagesQuery.selectActiveId()
        ]).pipe(map(([errors]) => {
            if (errors != null && errors.length > 0) {
                return errors;
            }
            return this._validationService.getLocalizedErrorMessages();
        }));
        /** container for @Input() label */
        this._label$$ = new BehaviorSubject(null);
        /**
         * link to formcontrol
         */
        this.formControlRef = null;
        /**
         * Custom error messages
         */
        this.errorMessages = [];
        this.label = null;
        this.showErrorType = 'group';
        this.calculatedErrors = new EventEmitter();
        this.calculatedErrors$$ = this._formControlRef$$.pipe(switchMap(ref => ref == null ? EMPTY : of(ref)), // fix console error
        switchMap(ref => combineLatest([
            of(ref),
            this._errorMessageContainer$$,
            ref.valueChanges.pipe(startWith(ref.value)),
            ref.statusChanges.pipe(startWith(ref.status)),
            this._updateValidityDependencies$$
        ])), map(([control, errorMessages]) => {
            const result = [];
            if (control.valid === false && errorMessages.length > 0 && control.errors != null) {
                const controlErrorsKeys = FFObject.keys(control.errors);
                // For correct display datepicker error
                const hasRequiredError = controlErrorsKeys.some(key => key === 'required');
                const hasMatDatepickerParseError = controlErrorsKeys.some(key => key === 'matDatepickerParse');
                controlErrorsKeys.filter(key => hasRequiredError && hasMatDatepickerParseError ? key !== 'required' : true)
                    .forEach(key => {
                    var _a, _b;
                    const errorMessage = (_a = errorMessages.find(x => key === x.error)) !== null && _a !== void 0 ? _a : null;
                    if (errorMessage != null && control.errors != null) {
                        // @ts-ignore
                        const errorString = errorMessage.format((_b = this._label$$.getValue()) !== null && _b !== void 0 ? _b : '', control.errors[key]);
                        result.push(errorString);
                    }
                    else {
                        result.push('validation msg localization not found');
                        throw new Error(`Key ${key} is not found in control errors`);
                    }
                });
            }
            if (this.showErrorType === 'single' && result.length > 1) {
                return result.slice(-1);
            }
            return result;
        }));
    }
    ngDoCheck() {
        var _a;
        if ((_a = this.formControlRef) === null || _a === void 0 ? void 0 : _a.touched) {
            this._updateValidityDependencies$$.next(randomString());
        }
    }
    ngOnChanges(changes) {
        nextOnChange(changes.formControlRef, this._formControlRef$$);
        nextOnChange(changes.errorMessages, this._errorMessages$$);
        nextOnChange(changes.label, this._label$$);
        nextOnChange(changes.updateValidityDependencies, this._updateValidityDependencies$$);
    }
    ngAfterViewInit() {
        this._subscription$ = this.calculatedErrors$$.pipe(distinctUntilChanged(), tap(errors => { var _a; return this.calculatedErrors.emit((_a = errors === null || errors === void 0 ? void 0 : errors.length) !== null && _a !== void 0 ? _a : 0); })).subscribe();
    }
    ngOnDestroy() {
        this._subscription$.unsubscribe();
    }
}
FFLocalizedMatErrorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFLocalizedMatErrorComponent, deps: [{ token: i1.ValidationLocalizationService }, { token: i1.LocalizationQuery }, { token: i1.LanguagesQuery }], target: i0.ɵɵFactoryTarget.Component });
FFLocalizedMatErrorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: FFLocalizedMatErrorComponent, selector: "ff-localized-mat-error", inputs: { formControlRef: "formControlRef", errorMessages: "errorMessages", label: "label", updateValidityDependencies: "updateValidityDependencies", showErrorType: "showErrorType" }, outputs: { calculatedErrors: "calculatedErrors" }, usesOnChanges: true, ngImport: i0, template: "<div *ngFor=\"let err of calculatedErrors$$ | async\">{{ err }}</div>\n", directives: [{ type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }], pipes: { "async": i2.AsyncPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFLocalizedMatErrorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ff-localized-mat-error', template: "<div *ngFor=\"let err of calculatedErrors$$ | async\">{{ err }}</div>\n" }]
        }], ctorParameters: function () { return [{ type: i1.ValidationLocalizationService }, { type: i1.LocalizationQuery }, { type: i1.LanguagesQuery }]; }, propDecorators: { formControlRef: [{
                type: Input
            }], errorMessages: [{
                type: Input
            }], label: [{
                type: Input
            }], updateValidityDependencies: [{
                type: Input
            }], showErrorType: [{
                type: Input
            }], calculatedErrors: [{
                type: Output
            }] } });

class FFLocalizedMatErrorModule {
}
FFLocalizedMatErrorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFLocalizedMatErrorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FFLocalizedMatErrorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFLocalizedMatErrorModule, declarations: [FFLocalizedMatErrorComponent], imports: [CommonModule], exports: [FFLocalizedMatErrorComponent] });
FFLocalizedMatErrorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFLocalizedMatErrorModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFLocalizedMatErrorModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [FFLocalizedMatErrorComponent],
                    exports: [FFLocalizedMatErrorComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { FFLocalizedMatErrorComponent, FFLocalizedMatErrorModule };
